"use client";
import { useEffect, useState } from "react";
import PropTypes from "prop-types";
import CustomImage from "../../atoms/customImage";
import Typography from "../../atoms/typography";
import { getLocalStorageItem, setLocalStorageItem } from "../../../utils/storage/localStorageUtil";
import { constants } from "../../../constants/pageConstants/pdpConstants";

/** */
const PDPKeyHighlights = ({ keyHighlightDetails }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [showPartialSlide, setShowPartialSlide] = useState(false);
  const [mounted, setMounted] = useState(false);

  /** */
  const normalizeKeyHighlightDetails = (data = {}) => {
    // FORMAT 1 → keyNameX / keyValueX
    const isStatsFromKeyName = Object.keys(data)?.some((key) => key.startsWith("keyName"));
    if (isStatsFromKeyName) {
      return Object.keys(data)
        .filter((key) => key?.startsWith("keyName"))
        .map((key) => {
          const index = key?.match(/\d+/)?.[0];
          return {
            label: data[`keyName${index}`],
            value: data[`keyValue${index}`],
          };
        })
        .filter((item) => item?.label && item?.value);
    }

    // FORMAT 2 → key : value
    return Object.entries(data).map(([label, value]) => ({
      label,
      value,
    }));
  };

  const formattedData = normalizeKeyHighlightDetails(keyHighlightDetails);

  useEffect(() => {
    setIsOpen(false);
  }, [keyHighlightDetails]);

  useEffect(() => {
    setMounted(true);
  }, []);
  /** -----------------------------
   *  Handle Partial Auto Slide (3 times)
   --------------------------------*/
  useEffect(() => {
    if (!mounted) return;

    const raw = getLocalStorageItem(constants.KEY_HIGHLIGHTS_COUNT);
    const parsed = raw ? JSON.parse(raw) : null;

    const now = Date.now();
    const isExpired = !parsed?.expiry || now > parsed.expiry;

    const nextCount = isExpired ? 1 : parsed.count + 1;
    const expiry = now + 24 * 60 * 60 * 1000;

    // Stop after 3 times
    if (nextCount > 3) return;

    setShowPartialSlide(true);

    const timer = setTimeout(() => {
      setShowPartialSlide(false);
    }, 1500);

    // Save updated count
    setLocalStorageItem(constants.KEY_HIGHLIGHTS_COUNT, JSON.stringify({ count: nextCount, expiry }));

    return () => clearTimeout(timer);
  }, [mounted]);

  /** -----------------------------
   *  Open / Close handlers
   --------------------------------*/
  const onToggle = (e) => {
    e.stopPropagation();
    setIsOpen((prev) => !prev);
  };

  /** */
  const onClose = (e) => {
    e.stopPropagation();
    setIsOpen(false);
  };

  /** -----------------------------
   *  Compute panel translation
   --------------------------------*/
  const getPanelTransform = () => {
    if (showPartialSlide) return "-translate-x-0";
    if (isOpen) return "translate-x-0";
    return "-translate-x-full";
  };

  return (
    <>
      {/* Backdrop */}
      <div
        className={`absolute left-0 top-0 z-50 h-full transition-all duration-300 ${isOpen ? "w-full" : "w-0"}`}
        onClick={onClose}
      />

      {/* Sliding Panel */}
      <div
        id="keysSection"
        className={`
          absolute left-0 top-0 z-50 flex h-full
          w-[50%] max-w-[300px] transform items-center bg-fnp-500
          bg-opacity-80 p-16 text-fnp-100 backdrop-blur-lg transition-transform duration-300 ease-in-out
          ${getPanelTransform()}
        `}
      >
        {/* Toggle Button */}
        <button
          onClick={onToggle}
          className="absolute -right-[112px] flex h-[26px] w-[200px] rotate-90 items-center justify-center
                     overflow-hidden rounded-tl-16 rounded-tr-16 bg-fnp-500 bg-opacity-80 backdrop-blur-lg"
        >
          <div
            className={`flex h-full items-center justify-center transition-transform duration-300 ${
              isOpen ? "-rotate-90" : "rotate-90"
            }`}
          >
            <CustomImage src="/icons/CaretDown.svg" width={16} height={16} alt="" />
          </div>
        </button>

        {/* Content */}
        <div className="flex w-full flex-col gap-16">
          <Typography Tag="p" classes="text-16 leading-18 font-600 text-fnp-100">
            Key Highlights
          </Typography>

          {formattedData.map(({ label, value }) => (
            <div key={label}>
              <Typography Tag="p" classes="text-14 leading-18 font-600 text-fnp-100 capitalize mb-[5px]">
                {label}
              </Typography>

              <Typography
                Tag="p"
                classes="
                  text-14 leading-18 font-400 text-fnp-100 
                  overflow-hidden text-ellipsis 
                  [display:-webkit-box] [-webkit-line-clamp:2] [-webkit-box-orient:vertical]
                "
              >
                {value}
              </Typography>
            </div>
          ))}
        </div>
      </div>
    </>
  );
};

PDPKeyHighlights.propTypes = {
  keyHighlightDetails: PropTypes.object.isRequired,
};
export default PDPKeyHighlights;
